import { useState, useEffect } from "react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { Logo } from "./Logo";
import { useTheme } from "../theme";

const LINKS = [
  { label: "Products", href: "#products" },
  { label: "Categories", href: "#categories" },
  { label: "About", href: "#about" },
  { label: "Inventors", href: "#inventor" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { theme, toggle } = useTheme();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const ThemeToggle = ({ testid }) => (
    <button
      data-testid={testid}
      onClick={toggle}
      aria-label="Toggle light and dark theme"
      className="grid h-10 w-10 place-items-center rounded-full border border-brand-border text-brand-text hover:text-[#8DC63F] hover:border-[#8DC63F]/50 transition-colors"
    >
      {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
    </button>
  );

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled
          ? "bg-brand-nav backdrop-blur-xl border-b border-brand-border"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-12 h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-home-link">
          <Logo />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="text-sm font-medium text-brand-muted hover:text-[#8DC63F] transition-colors"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <ThemeToggle testid="theme-toggle" />
          <a
            href="#products"
            data-testid="nav-cta-button"
            className="inline-flex items-center rounded-full bg-[#8DC63F] px-6 py-2.5 text-sm font-semibold text-[#050A15] hover:bg-[#7AB034] transition-colors"
          >
            Explore Products
          </a>
        </div>

        <div className="md:hidden flex items-center gap-2">
          <ThemeToggle testid="theme-toggle-mobile" />
          <button
            data-testid="mobile-menu-toggle"
            className="text-brand-text"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div
          data-testid="mobile-menu"
          className="md:hidden bg-brand-nav backdrop-blur-xl border-b border-brand-border px-6 py-6 flex flex-col gap-5"
        >
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              onClick={() => setOpen(false)}
              className="text-base font-medium text-brand-text hover:text-[#8DC63F]"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#products"
            onClick={() => setOpen(false)}
            className="inline-flex w-fit items-center rounded-full bg-[#8DC63F] px-6 py-2.5 text-sm font-semibold text-[#050A15]"
          >
            Explore Products
          </a>
        </div>
      )}
    </header>
  );
};
